import { supabase } from './client';
import type { Database } from '../../../types/supabase';

export type TaskWithProject = {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  due_date?: string | null;
  project_id?: string | null;
  department?: string | null;
  department_origin?: string | null;
  order_index?: number | null;
  entity_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  project?: {
    id: string;
    name: string;
  } | null;
  isProjectTask?: boolean;
};

export async function getTasks(department: string | undefined, entityId: string, includeProjects = false) {
  try {
    if (!entityId) {
      console.error('Entity ID is required for getTasks');
      return [];
    }

    console.log(`Getting tasks for department: ${department}, entityId: ${entityId}`);

    // First try to get tasks directly with entity_id
    const { data, error } = await supabase
      .from('project_tasks')
      .select(`
        *,
        project:projects(id, name)
      `)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tasks by entity_id:', error);
      
      // Fallback to getting tasks through projects relation
      const { data: projectTasks, error: projectError } = await supabase
        .from('project_tasks')
        .select(`
          *,
          project:projects!inner(id, name, entity_id)
        `)
        .eq('project:projects.entity_id', entityId)
        .order('created_at', { ascending: false });
      
      if (projectError) {
        console.error('Error fetching tasks through projects:', projectError);
        throw projectError;
      }
      
      // Filter by department if specified
      let filteredTasks = projectTasks || [];
      if (department && filteredTasks.length > 0) {
        filteredTasks = filteredTasks.filter(task => 
          !task.department || task.department === department
        );
      }
      
      return filteredTasks.map(task => ({
        ...task,
        isProjectTask: true
      })) as TaskWithProject[];
    }

    // Filter by department if specified
    let filteredTasks = data || [];
    if (department && filteredTasks.length > 0) {
      filteredTasks = filteredTasks.filter(task => 
        !task.department || task.department === department
      );
    }

    return filteredTasks.map(task => ({
      ...task,
      isProjectTask: true
    })) as TaskWithProject[];
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
}

export async function updateTask(taskId: string, updates: { status: string; isProjectTask?: boolean }) {
  try {
    const { data, error } = await supabase
      .from('project_tasks')
      .update({ status: updates.status })
      .eq('id', taskId)
      .select();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
}

export async function getTasksForProject(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('project_tasks')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching project tasks:', error);
    throw error;
  }
}

export async function createProjectTask(projectId: string, task: {
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  created_by: string; // This field is required by the database
  created_by: string; // This field is required by the database
  entity_id?: string;
}) {
  try {
    // Get the entity_id from the project first
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('entity_id')
      .eq('id', projectId)
      .single();
      
    if (projectError) throw projectError;
    
    const { data, error } = await supabase
      .from('project_tasks')
      .insert([{
        project_id: projectId,
        entity_id: project.entity_id, // Add entity_id from the project
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        due_date: task.due_date,
        created_by: task.created_by, // Ensure created_by is passed to the database
        created_by: task.created_by, // Ensure created_by is passed to the database
        department: 'workshop', // Set department to workshop
        order_index: 0 // Default order index
      }])
      .select();

    if (error) {
      throw error;
    }

    return data[0] as TaskWithProject;
  } catch (error) {
    console.error('Error creating project task:', error);
    throw error;
  }
}